import { Divider, Group, Modal, Text } from '@melio/penny';
import { FeatureFlags, useFeature } from '@melio/platform-feature-flags';
import { useMelioIntl } from '@melio/platform-i18n';
import { useDateUtils } from '@melio/platform-utils';
import { SubscriptionBillingCycleEnum } from '@melio/subscriptions-api';
import { useSubscription } from '@melio/subscriptions-sdk';

import { usePlanInfo } from '../../utils';
import { PlanUpgradeDetails } from './types';
import { DetailsRow } from './UpgradeModalDetailsRow';

type SubscriptionUpgradeModalActivityProps = {
  isOpen: boolean;
  onClose: () => void;
  planUpgradeDetails: PlanUpgradeDetails;
};

export const SubscriptionUpgradeModalActivity = ({
  isOpen,
  onClose,
  planUpgradeDetails,
}: SubscriptionUpgradeModalActivityProps) => {
  const { subscription } = useSubscription();
  const { billingCycle, planId } = planUpgradeDetails;
  const { planName } = usePlanInfo({ planId });
  const [isPlatformSubscriptionsEnabled] = useFeature(FeatureFlags.PlatformSubscriptions, false);
  const { formatMessage, formatCurrency, formatDate } = useMelioIntl();
  const isModalOpen = isOpen && isPlatformSubscriptionsEnabled;

  const { createDate } = useDateUtils();
  const onCancel = () => onClose();
  const currencyFormatter = (value: number) =>
    formatCurrency(value, undefined, {
      maximumFractionDigits: 2,
      minimumFractionDigits: 0,
    });
  const billingCycleLabel =
    billingCycle === SubscriptionBillingCycleEnum.Monthly
      ? formatMessage('activities.subscription.upgradeModal.cycle.monthly')
      : formatMessage('activities.subscription.upgradeModal.cycle.yearly');

  const dateText = formatMessage('activities.subscription.upgradeModal.dateRange', {
    startDate: formatDate(createDate('2025-01-01'), { dateStyle: 'medium' }),
    endDate: formatDate(createDate('2026-01-01'), { dateStyle: 'medium' }),
  });

  return (
    <Modal
      isOpen={isModalOpen}
      onClose={onClose}
      header={formatMessage('activities.subscription.upgradeModal.title', { planName })}
      primaryButton={{
        label: formatMessage('activities.subscription.upgradeModal.primaryButton'),
        onClick: onCancel,
        variant: 'primary',
      }}
      secondaryButton={{
        label: formatMessage('activities.subscription.upgradeModal.secondaryButton'),
        onClick: onClose,
        variant: 'tertiary',
      }}
      data-testid="subscription-upgrade-modal-activity"
    >
      <Group variant="vertical" spacing="m">
        <Group variant="horizontal" spacing="s" justifyContent="space-between">
          <Group variant="vertical" spacing="none" textAlign="start">
            <Text textStyle="body2Semi">{planName}</Text>
            <Text textStyle="body4" color="neutral.darker">
              {billingCycleLabel}
            </Text>
          </Group>
          <Group variant="vertical" spacing="none" textAlign="end">
            <Text textStyle="body2Semi">{currencyFormatter(55)}</Text>
            <Text textStyle="body4" color="neutral.darker">
              {formatMessage('activities.subscription.upgradeModal.priceDetails', {
                usersNumber: subscription.numberOfSeats,
                monthlyPricePerUser: currencyFormatter(33),
              })}
            </Text>
          </Group>
        </Group>
        <Divider />
        <DetailsRow label="Subtotal" value={currencyFormatter(100)} />
        <DetailsRow label="Taxes" value={currencyFormatter(233)} />
        <Divider />
        <Group variant="horizontal" spacing="s" justifyContent="space-between">
          <Group variant="vertical" spacing="none" textAlign="start">
            <Text textStyle="body2Semi">{formatMessage('activities.subscription.upgradeModal.total')}</Text>
            <Text textStyle="body4" color="neutral.darker">
              {dateText}
            </Text>
          </Group>
          <Group variant="vertical" spacing="none" textAlign="end">
            <Text textStyle="body1Semi">{currencyFormatter(100.22)}</Text>
          </Group>
        </Group>
      </Group>
    </Modal>
  );
};
